import { useState, useCallback } from "react";
import { useSelectedOrganisation } from "xplatform_shell/useSelectedOrganisation";
import { costApi } from "@services/costApi";
import type {
  CostSummaryViewModel,
  CostTimeSeriesViewModel,
  CostBreakdownViewModel,
  CostFilterParams,
  CostGranularity,
  CostDimension,
} from "@models/cost";
import { useAdminPermissions } from "./useAdminPermissions";

export interface ICostState {
  summary: CostSummaryViewModel | null;
  timeSeries: CostTimeSeriesViewModel | null;
  breakdown: CostBreakdownViewModel | null;
  isLoading: boolean;
  isSummaryLoading: boolean;
  isTimeSeriesLoading: boolean;
  isBreakdownLoading: boolean;
  error: string | null;
  fetchAll: (
    dimension: CostDimension,
    granularity: CostGranularity,
    filters?: CostFilterParams,
  ) => Promise<void>;
  fetchSummary: (filters?: CostFilterParams) => Promise<void>;
  fetchTimeSeries: (
    granularity: CostGranularity,
    startDate: string,
    endDate: string,
    filters?: Omit<CostFilterParams, "startDate" | "endDate">,
  ) => Promise<void>;
  fetchBreakdown: (
    dimension: CostDimension,
    filters?: CostFilterParams,
  ) => Promise<void>;
}

export const useCost = (): ICostState => {
  const { isOrgLeader } = useAdminPermissions();
  const { organisation } = useSelectedOrganisation();
  const [summary, setSummary] = useState<CostSummaryViewModel | null>(null);
  const [timeSeries, setTimeSeries] = useState<CostTimeSeriesViewModel | null>(
    null,
  );
  const [breakdown, setBreakdown] = useState<CostBreakdownViewModel | null>(
    null,
  );
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isTimeSeriesLoading, setIsTimeSeriesLoading] = useState(false);
  const [isBreakdownLoading, setIsBreakdownLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const organisationId = isOrgLeader ? organisation?.organisationId : undefined;

  const withOrgScope = useCallback(
    <T extends { organisationId?: string }>(filters?: T): T | undefined => {
      if (!organisationId) return filters;
      return { ...filters, organisationId } as T;
    },
    [organisationId],
  );

  const fetchSummary = useCallback(
    async (filters?: CostFilterParams) => {
      try {
        setIsSummaryLoading(true);
        const response = await costApi.getCostSummary(withOrgScope(filters));
        setSummary(response);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch cost summary";
        setError(errorMessage);
        console.error("Error fetching cost summary:", err);
      } finally {
        setIsSummaryLoading(false);
      }
    },
    [withOrgScope],
  );

  const fetchTimeSeries = useCallback(
    async (
      granularity: CostGranularity,
      startDate: string,
      endDate: string,
      filters?: Omit<CostFilterParams, "startDate" | "endDate">,
    ) => {
      try {
        setIsTimeSeriesLoading(true);
        const response = await costApi.getCostTimeSeries(
          granularity,
          startDate,
          endDate,
          withOrgScope(filters),
        );
        setTimeSeries(response);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch cost time series";
        setError(errorMessage);
        console.error("Error fetching cost time series:", err);
      } finally {
        setIsTimeSeriesLoading(false);
      }
    },
    [withOrgScope],
  );

  const fetchBreakdown = useCallback(
    async (dimension: CostDimension, filters?: CostFilterParams) => {
      try {
        setIsBreakdownLoading(true);
        const response = await costApi.getCostBreakdown(
          dimension,
          withOrgScope(filters),
        );
        setBreakdown(response);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch cost breakdown";
        setError(errorMessage);
        console.error("Error fetching cost breakdown:", err);
      } finally {
        setIsBreakdownLoading(false);
      }
    },
    [withOrgScope],
  );

  const fetchAll = useCallback(
    async (
      dimension: CostDimension,
      granularity: CostGranularity,
      filters?: CostFilterParams,
    ) => {
      setError(null);
      const scopedFilters = withOrgScope(filters);

      fetchSummary(filters);

      if (filters?.startDate && filters?.endDate) {
        fetchTimeSeries(granularity, filters.startDate, filters.endDate, {
          serviceName: scopedFilters?.serviceName,
          userId: scopedFilters?.userId,
          organisationId: scopedFilters?.organisationId,
          providerId: scopedFilters?.providerId,
        });
      }

      fetchBreakdown(dimension, filters);
    },
    [withOrgScope, fetchSummary, fetchTimeSeries, fetchBreakdown],
  );

  const isLoading = isSummaryLoading || isTimeSeriesLoading || isBreakdownLoading;

  return {
    summary,
    timeSeries,
    breakdown,
    isLoading,
    isSummaryLoading,
    isTimeSeriesLoading,
    isBreakdownLoading,
    error,
    fetchAll,
    fetchSummary,
    fetchTimeSeries,
    fetchBreakdown,
  };
};
